import { withScorers } from './simulatedScorers';
import { loadOracleScorers } from './scorerSquads';
import {
  DIRECT_KERNEL_VERSION,
  mulberry32,
  simulateDirectMatch,
} from "./directSimulationCore";
import { fetchFromApi } from "./fetchFromApi";

export const ORACLE_MODES = {
  oracle: { title: "DAS ORAKEL", subtitle: "Eine einmalige Direct Simulation" },
  exact: {
    title: "WAHRSCHEINLICHSTES ERGEBNIS",
    subtitle: "Das häufigste exakte Ergebnis der Direct Simulation",
  },
  tip: {
    title: "TIPPSPIEL-TIPP",
    subtitle: "Wahrscheinlichster Ausgang – passendes Ergebnis",
  },
};
export const ORACLE_TEMPOS = {
  short: { label: "KURZ", ms: 5500 },
  normal: { label: "NORMAL", ms: 8000 },
  long: { label: "LANG", ms: 12000 },
};
export const oracleKey = (userId, fixtureId) =>
  `betty:oracle:v1:${encodeURIComponent(userId)}:${fixtureId}`;
export const selectionKey = (userId) =>
  `betty:oracle:selection:v1:${encodeURIComponent(userId)}`;
export function readOracle(userId, fixtureId, storage = localStorage) {
  const raw = storage.getItem(oracleKey(userId, fixtureId));
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (
      value.fixtureId !== fixtureId ||
      !Array.isArray(value.result?.goalEvents)
    )
      throw new Error();
    return value;
  } catch {
    throw new Error(
      "Das gespeicherte Orakel ist nicht lesbar. Bitte keine neue Ziehung ohne bewusste Bestätigung starten."
    );
  }
}
export async function obtainOracle(
  userId,
  forecast,
  {
    replace = false,
    confirmed = false,
    storage = localStorage,
    now = Date.now(),
    loadScorers = loadOracleScorers,
  } = {}
) {
  const action = async () => {
    const existing = readOracle(userId, forecast.fixture.id, storage);
    if (existing && !replace) {
      if (existing.result.scorerVersion) return existing;
      const distributions = await loadScorers(forecast.fixture).catch(()=>({}));
      const enriched = {...existing,result:withScorers(existing.result,distributions,existing.seed)};
      storage.setItem(oracleKey(userId,forecast.fixture.id),JSON.stringify(enriched));
      return enriched;
    }
    if (replace && !confirmed)
      throw new Error("Ein neues Orakel benötigt deine Bestätigung.");
    if (Date.parse(forecast.fixture.kickoff) <= now)
      throw new Error(
        "Die Partie hat bereits begonnen. Ein neues Orakel ist nicht mehr möglich."
      );
    const seed = crypto.getRandomValues(new Uint32Array(1))[0];
    const { home, away, r } = forecast.basis;
    const value = {
      fixtureId: forecast.fixture.id,
      model: DIRECT_KERNEL_VERSION,
      basis: { ...forecast.basis },
      seed,
      createdAt: new Date(now).toISOString(),
      result: simulateDirectMatch(home, away, r, mulberry32(seed)),
    };
    value.result = withScorers(value.result, await loadScorers(forecast.fixture).catch(()=>({})), seed);
    // Do not display a draw if it could not be saved: reopening must not silently reroll.
    storage.setItem(
      oracleKey(userId, forecast.fixture.id),
      JSON.stringify(value)
    );
    return value;
  };
  return typeof navigator !== "undefined" && navigator.locks
    ? navigator.locks.request(oracleKey(userId, forecast.fixture.id), action)
    : action();
}
const normalize = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("de")
    .replace(/ß/g, "ss");
export function searchOracleFixtures(fixtures, query, now = Date.now()) {
  const terms = normalize(query)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  return fixtures.filter(
    (f) =>
      Date.parse(f.kickoff) > now &&
      terms.every((term) =>
        normalize(`${f.home.name} ${f.away.name}`).includes(term)
      )
  );
}
export function selectOracleFixture(selected, fixture) {
  if (selected.some((f) => f.id === fixture.id)) return selected;
  if (selected.length >= 3)
    throw new Error("Bitte zuerst einen der drei Plätze freigeben.");
  return [...selected, fixture].sort(
    (a, b) => Date.parse(a.kickoff) - Date.parse(b.kickoff)
  );
}
export function oracleDateRange(period, now = new Date()) {
  const from = new Date(now),
    to = new Date(now);
  if (period === "weekend") {
    const day = now.getDay();
    const offset = day === 0 ? -2 : day === 1 ? -3 : 5 - day;
    from.setDate(from.getDate() + offset);
    to.setTime(from.getTime());
    to.setDate(to.getDate() + 3);
  } else to.setDate(to.getDate() + 7);
  const date = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return { from: date(from), to: date(to) };
}
export function deterministicScore(forecast, mode) {
  const p = forecast.probabilities;
  const outcomes = [
    { key: "home", p: p.home },
    { key: "draw", p: p.draw },
    { key: "away", p: p.away },
  ];
  // Explicit stable ties: home, draw, away; exact scores: lower total, then home goals.
  const outcome = outcomes.reduce((a, b) => (b.p > a.p ? b : a)).key;
  const scores = forecast.correctScores
    ? Object.entries(forecast.correctScores).map(([score, probability]) => {
        const [home, away] = score.split(":").map(Number);
        return { home, away, probability };
      })
    : forecast.distribution.flatMap((row, home) =>
        row.map((probability, away) => ({ home, away, probability }))
      );
  const candidates = scores.filter(
    (s) =>
      s.probability > 0 &&
      (mode !== "tip" ||
        (outcome === "home"
          ? s.home > s.away
          : outcome === "draw"
          ? s.home === s.away
          : s.home < s.away))
  );
  candidates.sort(
    (a, b) =>
      b.probability - a.probability ||
      a.home + a.away - (b.home + b.away) ||
      a.home - b.home
  );
  if (!candidates.length)
    throw new Error("Keine passende Correct-Score-Auswertung vorhanden.");
  return { ...candidates[0], outcome };
}
export function scoreAtMinute(result, minute) {
  return result.goalEvents
    .filter((e) => e.minute <= minute)
    .reduce((score, e) => ({ ...score, [e.side]: score[e.side] + 1 }), {
      home: 0,
      away: 0,
    });
}
export async function oracleRequest(path, body) {
  const response = await fetchFromApi(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || !result.success)
    throw new Error(result.message || "Orakel konnte nicht geladen werden.");
  return result;
}

// Reuse the existing endpoint limit for a full league matchday; never truncate it.
export async function requestOracleForecasts(ids) {
  const forecasts = [];
  for (let offset = 0; offset < ids.length; offset += 3) {
    const result = await oracleRequest("oracle-forecasts", { fixture_ids: ids.slice(offset, offset + 3) });
    forecasts.push(...result.forecasts);
  }
  return { forecasts };
}

// Same per-fixture eligibility as the central oracle endpoint; no group-wide veto.
export function canOracleFixture(fixture, now = Date.now()) {
  const state = String(fixture.state?.state || fixture.state?.short_name || "").toUpperCase();
  return Number.isFinite(Date.parse(fixture.kickoff)) && Date.parse(fixture.kickoff) > now &&
    (fixture.state_id == null ? !state || state === "NS" : Number(fixture.state_id) === 1);
}
export async function requestPlayableLeagueForecasts(fixtures, now = Date.now()) {
  const results = await Promise.all(fixtures.filter(f => canOracleFixture(f, now)).map(async f => {
    try { return await requestOracleForecasts([f.id]); }
    catch (error) { return { forecasts: [], errors: { [f.id]: error.message } }; }
  }));
  return { forecasts: results.flatMap(r => r.forecasts), errors: Object.assign({}, ...results.map(r => r.errors || {})) };
}
